import { supabase } from './supabase';
import { ApartmentPartnership, Partner } from '../types';
import { toast } from 'sonner';
import * as Sentry from '@sentry/react';
import { getCachedPartnership, setCachedPartnership, invalidatePartnershipCache, isCacheValid } from './partnershipCache';

// Type alias for partnership used in components
export type Partnership = ApartmentPartnership;

/**
 * Past partner information
 */
export interface PastPartner {
  userId: string;
  name: string;
  email?: string;
  apartmentIds: string[]; // Which apartments they're partnered on
}

/**
 * Generate a unique invitation token
 */
function generateInvitationToken(): string {
  return `inv_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;
}

/**
 * Generate invitation link from token
 */
export function generateInvitationLink(_apartmentId: string, token: string): string {
  // Note: apartmentId kept for future deep linking
  return `${window.location.origin}/#/invite/${token}`;
}

/**
 * Add an apartment to a partner's favorites (matches table)
 * This is called automatically when a partnership is created/accepted
 * Uses upsert to handle duplicates gracefully
 */
async function addApartmentToPartnerFavorites(
  partnerId: string,
  apartmentId: string
): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('matches')
      .upsert({
        renter_id: partnerId,
        apartment_id: apartmentId,
      }, {
        onConflict: 'renter_id,apartment_id',
        ignoreDuplicates: true
      });

    if (error) {
      // Log error but don't throw - partnership creation should succeed even if favorite fails
      console.error('Error adding apartment to partner favorites:', error);
      Sentry.captureException(error, { 
        tags: { 
          feature: 'partnerships', 
          action: 'add-partner-favorite',
          partnerId,
          apartmentId
        } 
      });
      return false;
    }

    return true;
  } catch (error) {
    console.error('Unexpected error adding apartment to partner favorites:', error);
    Sentry.captureException(error as Error, { 
      tags: { 
        feature: 'partnerships', 
        action: 'add-partner-favorite' 
      } 
    });
    return false;
  }
}

/**
 * Check if partnership is accepted
 */
export function isPartnershipAccepted(partnership: Partnership): boolean {
  return partnership.status === 'accepted';
}

/**
 * Get partner name from partnership
 */
export async function getPartnerName(partnership: Partnership, currentUserId: string): Promise<string> {
  try {
    // Logic: Show the OTHER person's name, not your own
    if (partnership.inviter_id === currentUserId) {
      // I am the inviter → show invitee (partner)
      return (partnership as any).invitee_name || partnership.invitee_email?.split('@')[0] || 'Partner';
    } else {
      // I am the invitee → show inviter (partner)
      return (partnership as any).inviter_name || 'Partner';
    }
  } catch (error) {
    console.error('Error getting partner name:', error);
    Sentry.captureException(error, { tags: { feature: 'partnerships', action: 'get-partner-name' } });
    return 'Partner';
  }
}

/**
 * Get partnership for an apartment (for current user)
 * Uses cache to avoid expensive database queries on repeated calls
 */
export async function getPartnershipForApartment(apartmentId: string): Promise<Partnership | null> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return null;

    // Check cache first
    const cached = getCachedPartnership(user.id, apartmentId);
    if (cached && isCacheValid(cached.timestamp)) {
      return cached.partnership;
    }

    // Not in cache or expired - fetch from DB
    // Get partnership where user is inviter or invitee
    // Need to check both inviter_id and invitee_id separately due to RLS
    const { data: asInviter } = await supabase
      .from('apartment_partnerships')
      .select('*')
      .eq('apartment_id', apartmentId)
      .eq('inviter_id', user.id)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (asInviter) {
      // Cache the result
      setCachedPartnership(user.id, apartmentId, asInviter, isPartnershipAccepted(asInviter));
      return asInviter;
    }

    const { data: asInvitee } = await supabase
      .from('apartment_partnerships')
      .select('*')
      .eq('apartment_id', apartmentId)
      .eq('invitee_id', user.id)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    // Cache the result (even if null)
    setCachedPartnership(user.id, apartmentId, asInvitee, asInvitee ? isPartnershipAccepted(asInvitee) : false);

    return asInvitee;

  } catch (error) {
    console.error('Error fetching partnership:', error);
    Sentry.captureException(error, { tags: { feature: 'partnerships', action: 'fetch-partnership' } });
    return null;
  }
}

/**
 * Create a partnership invitation for an apartment
 * Returns { token: string, isNew: boolean }
 */
export async function createPartnershipInvitation(apartmentId: string): Promise<{ token: string; isNew: boolean }> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      throw new Error("User not logged in");
    }

    // Check if partnership already exists for this user and apartment
    const existing = await getPartnershipForApartment(apartmentId);
    if (existing && existing.inviter_id === user.id) {
      // Return existing token with isNew=false
      return { token: existing.invitation_token, isNew: false };
    }

    const token = generateInvitationToken();
    
    // Get inviter's name from user metadata
    const inviterName = user.user_metadata?.name || user.email?.split('@')[0] || 'User';
    
    // Try to insert with name (if column exists)
    let { error } = await supabase
      .from('apartment_partnerships')
      .insert({
        apartment_id: apartmentId,
        inviter_id: user.id,
        invitation_token: token,
        status: 'pending',
        inviter_name: inviterName
      });

    // If insert failed due to missing column, retry without name
    if (error && error.message?.includes('inviter_name')) {
      const { error: retryError } = await supabase
        .from('apartment_partnerships')
        .insert({
          apartment_id: apartmentId,
          inviter_id: user.id,
          invitation_token: token,
          status: 'pending'
        });
      
      if (retryError) throw retryError;
    } else if (error) {
      throw error;
    }

    // Invalidate cache for this apartment
    invalidatePartnershipCache(user.id, apartmentId);

    return { token, isNew: true };

  } catch (error: any) {
    console.error('Error creating partnership invitation:', error);
    Sentry.captureException(error, { tags: { feature: 'partnerships', action: 'create-invitation' } });
    toast.error('Failed to create invitation');
    throw error;
  }
}

/**
 * Get partnership invitation by token
 * Uses secure function that bypasses RLS (safe because token is secret)
 */
export async function getPartnershipByToken(token: string): Promise<ApartmentPartnership | null> {
  try {
    // Try RPC function first
    const { data: rpcData, error: rpcError } = await supabase
      .rpc('get_partnership_by_token', { token_param: token });

    if (!rpcError && rpcData && rpcData.length > 0) {
      return rpcData[0];
    }

    // Fallback: Try direct table query
    const { data: directData, error: directError } = await supabase
      .from('apartment_partnerships')
      .select('*')
      .eq('invitation_token', token)
      .maybeSingle();

    if (directError) {
      console.error('Error fetching partnership by token:', directError);
      return null;
    }

    return directData;
  } catch (error) {
    console.error('Unexpected error in getPartnershipByToken:', error);
    Sentry.captureException(error, { tags: { feature: 'partnerships', action: 'get-by-token' } });
    return null;
  }
}

/**
 * Accept a partnership invitation
 */
export async function acceptPartnershipInvitation(token: string): Promise<boolean> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      throw new Error("User not logged in");
    }

    // Get the partnership
    const partnership = await getPartnershipByToken(token);
    if (!partnership) {
      toast.error("Invalid invitation link");
      return false;
    }

    // ❌ PREVENT SELF-ACCEPTANCE: User cannot accept their own invitation
    if (partnership.inviter_id === user.id) {
      toast.error("You cannot accept your own invitation! Share this link with a partner.");
      return false;
    }

    // Check if already accepted by someone
    if (partnership.status === 'accepted') {
      if (partnership.invitee_id === user.id) {
        // Already accepted by this user
        return true;
      } else {
        // Already accepted by someone else
        toast.error("This invitation has already been accepted by another user");
        return false;
      }
    }

    // Check if invitation was declined
    if (partnership.status === 'declined') {
      toast.error("This invitation has been declined");
      return false;
    }

    // Check if invitation has expired
    const expiresAt = (partnership as any).expires_at;
    if (expiresAt && new Date(expiresAt) < new Date()) {
      toast.error("This invitation has expired");
      return false;
    }

    // Check if user is the intended invitee (if email was specified)
    if (partnership.invitee_email && partnership.invitee_email !== user.email) {
      toast.error(`This invitation was sent to ${partnership.invitee_email}`);
      return false;
    }

    // Get invitee's name from user metadata
    const inviteeName = user.user_metadata?.name || user.email?.split('@')[0] || 'User';
    
    // Use RPC function to bypass RLS (secure because token is the authentication)
    const { data: rpcData, error: rpcError } = await supabase
      .rpc('accept_partnership_by_token', {
        token_param: token,
        user_id_param: user.id,
        user_name_param: inviteeName
      });

    if (rpcError) {
      // Provide user-friendly error messages
      if (rpcError.message?.includes('Cannot accept your own invitation')) {
        toast.error("You cannot accept your own invitation! Share this link with a partner.");
        return false;
      } else if (rpcError.message?.includes('already been')) {
        toast.error("This invitation has already been accepted or declined");
        return false;
      } else if (rpcError.message?.includes('expired')) {
        toast.error("This invitation has expired");
        return false;
      } else if (rpcError.message?.includes('different email')) {
        toast.error("This invitation was sent to a different email address");
        return false;
      }
      
      console.error('Error accepting partnership:', rpcError);
      throw rpcError;
    }

    if (!rpcData || rpcData.length === 0) {
      throw new Error('Failed to accept partnership - no data returned');
    }

    // Invalidate cache for this apartment
    // Reuse the partnership we already fetched earlier (line 215)
    if (partnership) {
      invalidatePartnershipCache(user.id, partnership.apartment_id);
      
      // Automatically add apartment to current user's (invitee/partner's) favorites
      // The current user (who accepts) is the partner, so they should get the apartment in their favorites
      await addApartmentToPartnerFavorites(user.id, partnership.apartment_id);
    }

    toast.success("Partnership accepted!");
    return true;

  } catch (error: any) {
    console.error('Error accepting partnership:', error);
    Sentry.captureException(error, { tags: { feature: 'partnerships', action: 'accept-invitation' } });
    toast.error('Failed to accept invitation');
    return false;
  }
}

/**
 * Get partners for a specific apartment and user
 */
export async function getApartmentPartners(apartmentId: string): Promise<Partner[]> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return [];

    // Get partnerships where user is inviter
    const { data: asInviter } = await supabase
      .from('apartment_partnerships')
      .select('*')
      .eq('apartment_id', apartmentId)
      .eq('inviter_id', user.id)
      .eq('status', 'accepted');

    // Get partnerships where user is invitee
    const { data: asInvitee } = await supabase
      .from('apartment_partnerships')
      .select('*')
      .eq('apartment_id', apartmentId)
      .eq('invitee_id', user.id)
      .eq('status', 'accepted');

    const partnerships = [...(asInviter || []), ...(asInvitee || [])];
    if (partnerships.length === 0) return [];

    const partners: Partner[] = [];

    for (const partnership of partnerships) {
      // Determine who the partner is (the other person)
      const partnerId = partnership.inviter_id === user.id 
        ? partnership.invitee_id 
        : partnership.inviter_id;
      
      const role = partnership.inviter_id === user.id ? 'invitee' : 'inviter';
      const partnerEmail = partnership.invitee_email;

      if (!partnerId && !partnerEmail) continue;

      // We can't directly query auth.users, so we'll use the stored email
      // and rely on user metadata updates when they accept
      partners.push({
        id: partnerId || '',
        email: partnerEmail || '',
        name: undefined, // Will be undefined until we implement a profiles table
        role
      });
    }

    return partners;

  } catch (error) {
    console.error('Error fetching apartment partners:', error);
    Sentry.captureException(error, { tags: { feature: 'partnerships', action: 'fetch-partners' } });
    return [];
  }
}

/**
 * Check if current user has a partnership for an apartment
 */
export async function hasApartmentPartnership(apartmentId: string): Promise<boolean> {
  const partners = await getApartmentPartners(apartmentId);
  return partners.length > 0;
}

/**
 * Get pending invitations sent by current user
 */
export async function getPendingInvitations(apartmentId: string): Promise<ApartmentPartnership[]> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return [];

    const { data, error } = await supabase
      .from('apartment_partnerships')
      .select('*')
      .eq('apartment_id', apartmentId)
      .eq('inviter_id', user.id)
      .eq('status', 'pending');

    if (error) throw error;
    return data || [];

  } catch (error) {
    console.error('Error fetching pending invitations:', error);
    Sentry.captureException(error, { tags: { feature: 'partnerships', action: 'fetch-pending' } });
    return [];
  }
}

/**
 * Decline a partnership invitation
 * Uses RPC to bypass RLS (invitee_id is NULL before accept, so direct UPDATE would fail)
 */
export async function declinePartnershipInvitation(token: string): Promise<boolean> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      throw new Error("User not logged in");
    }

    // Use RPC to bypass RLS (invitee_id is NULL before accept)
    const { error: rpcError } = await supabase
      .rpc('decline_partnership_by_token', {
        token_param: token,
        user_id_param: user.id
      });

    if (rpcError) {
      // Handle specific error messages from the RPC function
      if (rpcError.message?.includes('Cannot decline your own')) {
        toast.error("You cannot decline your own invitation");
        return false;
      }
      if (rpcError.message?.includes('already been')) {
        toast.error("This invitation has already been processed");
        return false;
      }
      if (rpcError.message?.includes('not found')) {
        toast.error("Invalid invitation");
        return false;
      }
      throw rpcError;
    }

    toast.success("Invitation declined");
    return true;

  } catch (error: any) {
    console.error('Error declining partnership:', error);
    Sentry.captureException(error, { tags: { feature: 'partnerships', action: 'decline-invitation' } });
    toast.error('Failed to decline invitation');
    return false;
  }
}

/**
 * Revoke (cancel) a partnership invitation you created
 */
export async function revokePartnershipInvitation(apartmentId: string): Promise<boolean> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      throw new Error("User not logged in");
    }

    // Delete pending invitations created by this user for this apartment
    const { error } = await supabase
      .from('apartment_partnerships')
      .delete()
      .eq('apartment_id', apartmentId)
      .eq('inviter_id', user.id)
      .eq('status', 'pending');

    if (error) throw error;

    // Invalidate cache for this apartment
    invalidatePartnershipCache(user.id, apartmentId);

    toast.success("Invitation cancelled");
    return true;

  } catch (error: any) {
    console.error('Error revoking partnership:', error);
    Sentry.captureException(error, { tags: { feature: 'partnerships', action: 'revoke-invitation' } });
    toast.error('Failed to cancel invitation');
    return false;
  }
}

/**
 * Remove an accepted partnership
 */
export async function removePartnership(apartmentId: string): Promise<boolean> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      throw new Error("User not logged in");
    }

    // Can only delete if you're the inviter
    const { error } = await supabase
      .from('apartment_partnerships')
      .delete()
      .eq('apartment_id', apartmentId)
      .eq('inviter_id', user.id);

    if (error) throw error;

    // Invalidate cache for this apartment
    invalidatePartnershipCache(user.id, apartmentId);

    toast.success("Partnership removed");
    return true;

  } catch (error: any) {
    console.error('Error removing partnership:', error);
    Sentry.captureException(error, { tags: { feature: 'partnerships', action: 'remove-partnership' } });
    toast.error('Failed to remove partnership');
    return false;
  }
}

/**
 * Get all past partners from liked apartments with accepted partnerships
 * Returns unique partners with their names and which apartments they're partnered on
 */
export async function getPastPartners(): Promise<PastPartner[]> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return [];

    // Step 1: Fetch all apartments the current user has liked
    const { data: matchesData, error: matchesError } = await supabase
      .from('matches')
      .select('apartment_id')
      .eq('renter_id', user.id);

    if (matchesError) throw matchesError;
    if (!matchesData || matchesData.length === 0) return [];

    const likedApartmentIds = matchesData.map(m => m.apartment_id);

    // Step 2: Fetch all accepted partnerships for these apartments where user is involved
    const { data: asInviter, error: inviterError } = await supabase
      .from('apartment_partnerships')
      .select('*')
      .in('apartment_id', likedApartmentIds)
      .eq('inviter_id', user.id)
      .eq('status', 'accepted');

    if (inviterError) throw inviterError;

    const { data: asInvitee, error: inviteeError } = await supabase
      .from('apartment_partnerships')
      .select('*')
      .in('apartment_id', likedApartmentIds)
      .eq('invitee_id', user.id)
      .eq('status', 'accepted');

    if (inviteeError) throw inviteeError;

    const allPartnerships = [...(asInviter || []), ...(asInvitee || [])];
    if (allPartnerships.length === 0) return [];

    // Step 3: Extract unique partners and aggregate their apartment IDs
    const partnerMap = new Map<string, PastPartner>();

    for (const partnership of allPartnerships) {
      // Determine who the partner is (the other person)
      const partnerId = partnership.inviter_id === user.id 
        ? partnership.invitee_id 
        : partnership.inviter_id;

      if (!partnerId) continue; // Skip if no partner ID

      // Get partner name
      const partnerName = partnership.inviter_id === user.id
        ? ((partnership as any).invitee_name || partnership.invitee_email?.split('@')[0] || 'Partner')
        : ((partnership as any).inviter_name || 'Partner');

      // Get partner email
      const partnerEmail = partnership.inviter_id === user.id
        ? partnership.invitee_email
        : undefined; // We don't store inviter email in the table

      if (partnerMap.has(partnerId)) {
        // Partner already exists, add this apartment to their list
        const existing = partnerMap.get(partnerId)!;
        if (!existing.apartmentIds.includes(partnership.apartment_id)) {
          existing.apartmentIds.push(partnership.apartment_id);
        }
      } else {
        // New partner, create entry
        partnerMap.set(partnerId, {
          userId: partnerId,
          name: partnerName,
          email: partnerEmail,
          apartmentIds: [partnership.apartment_id]
        });
      }
    }

    // Convert map to array
    return Array.from(partnerMap.values());

  } catch (error) {
    console.error('Error fetching past partners:', error);
    Sentry.captureException(error, { tags: { feature: 'partnerships', action: 'fetch-past-partners' } });
    return [];
  }
}

/**
 * Create an accepted partnership between current user and a partner
 * Automatically sets both inviter_id and invitee_id with status='accepted'
 */
export async function createAcceptedPartnership(
  apartmentId: string,
  partnerId: string,
  partnerName: string
): Promise<Partnership | null> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      throw new Error("User not logged in");
    }

    // Check if partnership already exists for this apartment
    const existing = await getPartnershipForApartment(apartmentId);
    
    // If there's a pending partnership created by current user, update it instead of creating new
    if (existing && existing.status === 'pending' && existing.inviter_id === user.id && !existing.invitee_id) {
      const now = new Date().toISOString();
      const currentUserName = user.user_metadata?.name || user.email?.split('@')[0] || 'User';
      
      // Update the existing pending partnership to accepted with the selected partner
      let { data, error } = await supabase
        .from('apartment_partnerships')
        .update({
          invitee_id: partnerId,
          status: 'accepted',
          accepted_at: now,
          invitee_name: partnerName
        })
        .eq('id', existing.id)
        .select()
        .single();

      if (error) {
        // If update failed due to missing column, retry without name
        if (error.message?.includes('invitee_name')) {
          const { data: retryData, error: retryError } = await supabase
            .from('apartment_partnerships')
            .update({
              invitee_id: partnerId,
              status: 'accepted',
              accepted_at: now
            })
            .eq('id', existing.id)
            .select()
            .single();

          if (retryError) throw retryError;
          data = retryData;
        } else {
          throw error;
        }
      }

      // Invalidate cache for this apartment
      invalidatePartnershipCache(user.id, apartmentId);
      
      // Automatically add apartment to partner's favorites
      await addApartmentToPartnerFavorites(partnerId, apartmentId);
      
      return data;
    }
    
    // If there's an accepted partnership, don't create a new one
    if (existing && existing.status === 'accepted') {
      toast.error("A partnership already exists for this apartment");
      return null;
    }

    const token = generateInvitationToken();
    const now = new Date().toISOString();
    
    // Get current user's name
    const currentUserName = user.user_metadata?.name || user.email?.split('@')[0] || 'User';

    // Create the partnership with both parties already set (auto-accepted)
    const { data, error } = await supabase
      .from('apartment_partnerships')
      .insert({
        apartment_id: apartmentId,
        inviter_id: user.id,
        invitee_id: partnerId,
        invitation_token: token,
        status: 'accepted',
        accepted_at: now,
        inviter_name: currentUserName,
        invitee_name: partnerName
      })
      .select()
      .single();

    if (error) {
      // If the error is about missing columns, retry without the name columns
      if (error.message?.includes('inviter_name') || error.message?.includes('invitee_name')) {
        const { data: retryData, error: retryError } = await supabase
          .from('apartment_partnerships')
          .insert({
            apartment_id: apartmentId,
            inviter_id: user.id,
            invitee_id: partnerId,
            invitation_token: token,
            status: 'accepted',
            accepted_at: now
          })
          .select()
          .single();

        if (retryError) throw retryError;
        
        // Invalidate cache for this apartment
        invalidatePartnershipCache(user.id, apartmentId);
        
        // Automatically add apartment to partner's favorites
        await addApartmentToPartnerFavorites(partnerId, apartmentId);
        
        return retryData;
      }
      throw error;
    }

    // Invalidate cache for this apartment
    invalidatePartnershipCache(user.id, apartmentId);

    // Automatically add apartment to partner's favorites
    await addApartmentToPartnerFavorites(partnerId, apartmentId);

    return data;

  } catch (error: any) {
    console.error('Error creating accepted partnership:', error);
    Sentry.captureException(error, { tags: { feature: 'partnerships', action: 'create-accepted-partnership' } });
    toast.error('Failed to add partner');
    return null;
  }
}
